import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  ToastIntent,
  useId,
  ToastTrigger,
  Button,
} from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';

const SNACKBAR_DURATIONS = {
  SUCCESS: 8000,
  ERROR: 8000,
  WARNING: 8000,
  INFO: 8000,
  DEFAULT: 8000,
} as const;

export interface SnackbarContextType {
  showSuccess: (title: string, body?: string) => void;
  showError: (title: string, body?: string) => void;
  showWarning: (title: string, body?: string) => void;
  showInfo: (title: string, body?: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  const showToast = useCallback(
    (
      title: string,
      body: string | undefined,
      intent: ToastIntent = 'info',
      autoHideDuration: number = SNACKBAR_DURATIONS.DEFAULT,
    ) => {
      const dismissLabel = `Dismiss ${intent} notification`;

      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              <ToastTrigger>
                <Button size={'small'} appearance="subtle" icon={<Dismiss12Regular />} aria-label={dismissLabel} />
              </ToastTrigger>
            }
          >
            {title}
          </ToastTitle>
          {body && <ToastBody>{body}</ToastBody>}
        </Toast>,
        { intent, timeout: autoHideDuration },
      );
    },
    [dispatchToast],
  );

  // Convenience methods
  const showSuccess = useCallback(
    (title: string, body?: string) => {
      showToast(title, body, 'success', SNACKBAR_DURATIONS.SUCCESS);
    },
    [showToast],
  );

  const showError = useCallback(
    (title: string, body?: string) => {
      showToast(title, body, 'error', SNACKBAR_DURATIONS.ERROR);
    },
    [showToast],
  );

  const showWarning = useCallback(
    (title: string, body?: string) => {
      showToast(title, body, 'warning', SNACKBAR_DURATIONS.WARNING);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (title: string, body?: string) => {
      showToast(title, body, 'info', SNACKBAR_DURATIONS.INFO);
    },
    [showToast],
  );

  const value: SnackbarContextType = useMemo(
    () => ({ showSuccess, showError, showWarning, showInfo }),
    [showSuccess, showError, showWarning, showInfo],
  );

  return (
    <SnackbarContext.Provider value={value}>
      <Toaster toasterId={toasterId} position="top-end" offset={{ horizontal: 8, vertical: 56 }} />
      {children}
    </SnackbarContext.Provider>
  );
};
